import React, { useState } from 'react';
import clsx from 'clsx';
import { AlertCircle, Loader2, Plus, X } from 'lucide-react';
import { addDossierField, type AddableFieldKind } from '../../orbit/lib/intakeApi';

/**
 * "Add anything we missed", on every card.
 *
 * The registry has always supported per-company added fields, with a rule that
 * one can never gate routing — it was just admin-only, reachable through Field
 * Studio, where the author types the key by hand. The person filling in a card
 * is not thinking in keys, so this asks for the thing in their own words and
 * the backend derives the key from it. What they typed becomes the label.
 *
 * The kinds offered are the ones a person can describe without being told what
 * a field type is. `select`/`multiselect` are addable in the registry but need
 * an option list, which is a bigger ask than this control should make.
 */

const KINDS: { value: AddableFieldKind; label: string; hint: string }[] = [
  { value: 'text',     label: 'Short answer', hint: 'A word or a line' },
  { value: 'longtext', label: 'Long answer',  hint: 'A paragraph' },
  { value: 'number',   label: 'A number',     hint: 'Just digits' },
  { value: 'url',      label: 'A link',       hint: 'A web address' },
  { value: 'date',     label: 'A date',       hint: 'A day on the calendar' },
];

export interface AddFieldControlProps {
  /** Section the new field lands in, e.g. 'a'. */
  section: string;
  clientId?: string;
  /** Called after a successful add so the caller can refresh its schema. */
  onAdded: (key: string) => void;
  /** Override the collapsed prompt — the catch-all step words it differently. */
  label?: string;
  /** Ask which section it belongs to, for the catch-all step. */
  sectionPicker?: { value: string; label: string }[];
  onSectionChange?: (section: string) => void;
}

export const AddFieldControl: React.FC<AddFieldControlProps> = ({
  section, clientId, onAdded, label = 'Add anything we missed',
  sectionPicker, onSectionChange,
}) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [kind, setKind] = useState<AddableFieldKind>('text');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = text.trim().length >= 2;

  const close = () => {
    setOpen(false);
    setText('');
    setKind('text');
    setError(null);
  };

  const submit = async () => {
    if (!ready || saving) return;
    setSaving(true);
    setError(null);
    try {
      const { field } = await addDossierField({
        section, label: text.trim(), kind, clientId,
      });
      onAdded(field.key);
      close();
    } catch (e) {
      setError(messageFor(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-brand-600 whitespace-nowrap"
      >
        <Plus size={13} strokeWidth={2.5} />
        {label}
      </button>
    );
  }

  return (
    <div className="rounded-xl bg-brand-50/50 p-3.5 ring-1 ring-brand-100">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <label htmlFor="add-field-label" className="text-[12.5px] font-semibold text-gray-900">
          What should we call it?
        </label>
        <button
          type="button"
          onClick={close}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700"
          aria-label="Cancel"
        >
          <X size={13} strokeWidth={2.25} />
        </button>
      </div>

      <input
        id="add-field-label"
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && ready) void submit(); }}
        autoFocus
        placeholder="Busiest month of the year"
        className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      />
      <p className="mt-1.5 text-[11.5px] leading-snug text-gray-500">
        Write it the way you'd say it out loud. That becomes its name.
      </p>

      {sectionPicker && (
        <div className="mt-3">
          <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            Where does it belong?
          </span>
          <div className="flex flex-wrap gap-1.5">
            {sectionPicker.map(s => (
              <button
                key={s.value}
                type="button"
                onClick={() => onSectionChange?.(s.value)}
                className={clsx(
                  'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  s.value === section
                    ? 'border-brand-500 bg-brand-500 text-white'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-brand-500 hover:bg-white',
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3">
        <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          What kind of answer?
        </span>
        <div className="flex flex-wrap gap-1.5">
          {KINDS.map(k => (
            <button
              key={k.value}
              type="button"
              onClick={() => setKind(k.value)}
              title={k.hint}
              className={clsx(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                kind === k.value
                  ? 'border-brand-500 bg-brand-500 text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-brand-500 hover:bg-white',
              )}
            >
              {k.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-2.5 inline-flex items-start gap-1.5 text-[11.5px] font-medium text-red-600">
          <AlertCircle size={12} strokeWidth={2.5} className="mt-px flex-none" />
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={() => { void submit(); }}
        disabled={!ready || saving}
        className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-3.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-500 disabled:cursor-not-allowed disabled:bg-gray-300 whitespace-nowrap"
      >
        {saving && <Loader2 size={12} strokeWidth={2.5} className="animate-spin" />}
        {saving ? 'Adding' : 'Add it'}
      </button>
    </div>
  );
};

function messageFor(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (e instanceof Error && e.message) return e.message;
  return "Couldn't add that.";
}

export default AddFieldControl;
